<script setup lang="ts" generic="T extends string | number">
import { RadioGroupIndicator, RadioGroupItem, RadioGroupRoot } from 'reka-ui'
import { computed, useId } from 'vue'

import './base.css'
import { useFieldContext } from './field-context'
import type { SelectOption } from './types'

const props = withDefaults(
  defineProps<{
    options: readonly SelectOption<T>[]
    /** `horizontal` also switches arrow-key navigation to left/right. */
    orientation?: 'vertical' | 'horizontal'
    disabled?: boolean
    /** Accessible group name when the group is not wrapped in an `EField`. */
    ariaLabel?: string
  }>(),
  { orientation: 'vertical', ariaLabel: '' },
)

const model = defineModel<T | null>({ default: null })

const field = useFieldContext()
const fallbackId = useId()

const groupId = computed(() => field?.controlId.value ?? fallbackId)
const isDisabled = computed(() => props.disabled === true || field?.disabled.value === true)

const selected = computed<T | null>({
  get: () => model.value,
  set: (value) => {
    model.value = value ?? null
  },
})
</script>

<template>
  <RadioGroupRoot
    :id="groupId"
    v-model="selected"
    class="e-radio-group"
    :class="`e-radio-group--${orientation}`"
    :orientation="orientation"
    :disabled="isDisabled"
    :aria-label="ariaLabel === '' ? undefined : ariaLabel"
    :aria-describedby="field?.describedBy.value"
    :aria-invalid="field?.invalid.value ? 'true' : undefined"
  >
    <div v-for="option in options" :key="String(option.value)" class="e-radio">
      <RadioGroupItem
        :id="`${groupId}-${String(option.value)}`"
        :value="option.value"
        :disabled="(option.disabled ?? false) || isDisabled"
        class="e-radio__button"
      >
        <RadioGroupIndicator class="e-radio__dot" />
      </RadioGroupItem>
      <label :for="`${groupId}-${String(option.value)}`" class="e-radio__label">
        <span>{{ option.label }}</span>
        <span v-if="option.hint" class="e-radio__hint">{{ option.hint }}</span>
      </label>
    </div>
  </RadioGroupRoot>
</template>

<style>
.e-radio-group {
  display: flex;
  gap: var(--erpx-space-2);
}

.e-radio-group--vertical {
  flex-direction: column;
}

.e-radio-group--horizontal {
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--erpx-space-4);
}

.e-radio {
  display: flex;
  align-items: flex-start;
  gap: var(--erpx-space-2);
  min-width: 0;
}

.e-radio__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 16px;
  height: 16px;
  margin-block-start: 2px;
  background-color: var(--erpx-color-surface-raised);
  border: 1px solid var(--erpx-color-border-strong);
  border-radius: var(--erpx-radius-full);
  cursor: pointer;
  transition: border-color var(--erpx-duration-fast) var(--erpx-easing-standard);
}

.e-radio__button:focus-visible {
  outline: none;
  box-shadow: var(--erpx-focus-ring);
}

.e-radio__button[data-state='checked'] {
  border-color: var(--erpx-color-accent-solid);
}

.e-radio__button[data-disabled] {
  background-color: var(--erpx-color-surface-disabled);
  cursor: not-allowed;
}

.e-radio__dot {
  display: block;
  width: 8px;
  height: 8px;
  background-color: var(--erpx-color-accent-solid);
  border-radius: var(--erpx-radius-full);
}

.e-radio__label {
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: var(--erpx-text-base);
  line-height: var(--erpx-leading-snug);
  cursor: pointer;
  min-width: 0;
}

.e-radio__hint {
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-xs);
}
</style>
